/**
 * Vista completa de detalle del candidato
 * Permite visualizar toda la información del candidato y gestionar entrevistas RH
 */
import { Badge } from '@/shared/components/Badge'
import { colorDeEstatusDeCandidato } from '@/features/rh/lib/colorDeEstatusDeCandidato'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { Checkbox } from '@/shared/components/Checkbox'
import React, { useState, useEffect } from 'react'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  User,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  CheckCheck,
  CalendarPlus,
  FileText,
  MessageSquare,
  Paperclip,
  MapPin,
  Video,
  Trash2,
  Save,
  Plus,
  History
} from 'lucide-react'
import { comoInstante, formatDate, parseDateSafe } from '@/shared/lib/dateUtils'
import { EsqueletoDetalle } from '@/shared/components/Esqueleto'
import { EntrevistasDeDireccion } from '@/features/rh/components/EntrevistasDeDireccion'
import { DatosDelCandidato, DatosDelCandidatoEnConsulta } from '@/features/rh/components/hr-interviews/DatosDelCandidato'
import {
  aImporte,
  conLoDeclaradoPorElCandidato,
  formatoDeSueldo,
} from '@/features/rh/lib/datosDelCandidato'
import { etiquetaDeEstatus as nombreDeEstatus } from '@/features/rh/lib/estatusDelCandidato'
import { useContador } from '@/shared/hooks/useContador'
import { apiService } from '@/services/api'
import { hrInterviewService } from '@/features/rh/api/hrInterviewService'
import { fileService } from '@/services/fileService'
import { config } from '@/config/env'
import { useAuth } from '@/shared/hooks/useAuth'
import { canConductInterviews, hasAnyRole, puedeVerCompensacion, userHasPermission } from '@/shared/lib/roleUtils'
import type { Candidate, CandidateComment, PreviousApplication } from '@/services/api'
import { comentariosDeLaFicha } from '@/features/rh/lib/comentariosDeLaFicha'
import type {
  HRInterview,
  HRInterviewRegistration,
  HRInterviewResults,
  HRInterviewFormErrors,
  HRInterviewResultsFormErrors,
  DecisionDeEntrevistaEnCaptura
} from '@/types/hrInterview'
import AuthenticatedLayout from '@/shared/components/AuthenticatedLayout'
import { nombreDeUsuario } from '@/shared/lib/nombreDeUsuario'
import { ScheduleHiringManagerInterview } from '@/features/rh/components/HiringManagerInterviews'
import { RecomendacionDelCandidato } from '@/features/rh/components/RecomendacionDelCandidato'
import { InformacionGeneralDelCandidato } from '@/features/rh/components/InformacionGeneralDelCandidato'
import { EvaluacionPendienteDelCandidato } from '@/features/rh/components/EvaluacionPendienteDelCandidato'
import { tieneEvaluacionPendiente } from '@/features/rh/lib/evaluacionPendiente'
import {
  puedeAgendarEntrevistaDelSolicitante,
  textoDeLaEntrevistaDelSolicitante,
} from '@/features/rh/lib/agendaDelSolicitante'
import { SIN_ORIGEN } from '@/features/rh/lib/origenDelCandidato'
import { experienciaDelCandidato } from "@/features/rh/lib/experienciaDelCandidato";

const CandidateDetailView: React.FC = () => {
  const navigate = useNavigate()
  const { candidateId } = useParams<{ candidateId: string }>()
  const { user, logout } = useAuth()

  const [candidate, setCandidate] = useState<Candidate | null>(null)
  const [hrInterview, setHrInterview] = useState<HRInterview | null>(null)
  // RH-07: postulaciones anteriores de la misma persona, por coincidencia de
  // correo o telefono. Es informativo y de solo lectura; si la consulta falla,
  // la ficha se muestra igual (ver el catch de la carga).
  const [postulacionesPrevias, setPostulacionesPrevias] = useState<PreviousApplication[]>([])
  // null = todavia no llegan o el endpoint fallo: el tab arma lo que puede con la ficha.
  const [comentariosDelBack, setComentariosDelBack] = useState<CandidateComment[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Estados para el formulario de entrevista
  const [showInterviewForm, setShowInterviewForm] = useState(false)
  // El modal de agendar la entrevista con el solicitante, que ya existia
  // y se abria solo desde la vista de entrevistas-jefe (RH-03).
  const [showSolicitarEntrevista, setShowSolicitarEntrevista] = useState(false)
  const [interviewForm, setInterviewForm] = useState<HRInterviewRegistration>({
    candidateId: '',
    fechaEntrevista: '',
    horaEntrevista: '',
    modalidad: 'presencial',
    ubicacionLink: '',
    entrevistadorId: '',
    observaciones: ''
  })
  const [interviewErrors, setInterviewErrors] = useState<HRInterviewFormErrors>({})
  const [isSubmittingInterview, setIsSubmittingInterview] = useState(false)

  // Estados para el formulario de resultados
  const [showResultsForm, setShowResultsForm] = useState(false)
  const [resultsForm, setResultsForm] = useState<HRInterviewResults>({
    resultadoEntrevistaRh: 5,
    comentariosEntrevistaRh: '',
    camposClaveTest: '',
    cumplePerfil: false,
    experienciaValidada: false,
    antecedentesOk: false,
    // Sin decision de arranque: traerla en «Pendiente» era lo que dejaba
    // guardar resultados sin que nadie decidiera (SRH-307).
    decision: '',
    motivoRechazo: '',
    previousMonthlySalary: null,
    salaryExpectation: null,
    employmentStatus: null,
    availability: '',
    residenceCity: ''
  })
  /**
   * La decision con la que abre el formulario al reabrirlo.
   *
   * «Pendiente» guardada se trata como «sin decidir»: si se precargara, el
   * formulario volveria a salir con una respuesta puesta que nadie eligio, que
   * es justo lo que SRH-307 vino a quitar.
   */
  const decisionParaElFormulario = (
    guardada?: string | null,
  ): DecisionDeEntrevistaEnCaptura =>
    guardada === 'aprobado' || guardada === 'rechazado' ? guardada : ''

  const [resultsErrors, setResultsErrors] = useState<HRInterviewResultsFormErrors>({})
  const [isSubmittingResults, setIsSubmittingResults] = useState(false)

  // Estados para upload de archivo
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null)
  const [isUploadingFile, setIsUploadingFile] = useState(false)

  // Estados para cambio de estado del candidato
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false)
  const [showStatusSuccessModal, setShowStatusSuccessModal] = useState(false)
  const [statusSuccessMessage, setStatusSuccessMessage] = useState('')

  // Estados para decisión del jefe requisitor
  const [showHiringManagerDecisionModal, setShowHiringManagerDecisionModal] = useState(false)
  const [hiringManagerDecision, setHiringManagerDecision] = useState<'aprobar' | 'rechazar' | null>(null)
  const [hiringManagerComments, setHiringManagerComments] = useState('')
  const [isSubmittingHiringDecision, setIsSubmittingHiringDecision] = useState(false)

  // Estado para controlar la tab activa (experiencia, entrevista-rh o cv)
  // A-22: 700 ms para la barra de avance, como pide el recetario.
  const avance = useContador<HTMLDivElement>(hrInterview?.resultadoEntrevistaRh ?? 0, 700)
  const [activeTab, setActiveTab] = useState<'informacion-general' | 'entrevista-rh' | 'cv' | 'comentarios' | 'historial'>('informacion-general')

  // ── Visor del CV ───────────────────────────────────────────────────────
  // El archivo se trae como blob y se muestra desde una URL del mismo origen.
  // Antes el iframe apuntaba directo al API y en produccion el navegador lo
  // bloqueaba: nginx manda X-Frame-Options SAMEORIGIN en el dominio del API, y
  // ahi el front vive en otro dominio. En local no se veia porque todo pasa
  // por el proxy /api-proxy y el iframe queda en el mismo origen.
  //
  // Es el mismo camino que ya usan los visores de documentos del empleado.
  const [cvBlobUrl, setCvBlobUrl] = useState<string | null>(null)
  const [cvCargando, setCvCargando] = useState(false)
  const [cvError, setCvError] = useState<string | null>(null)

  // Estado para entrevistadores disponibles
  const [availableInterviewers, setAvailableInterviewers] = useState<Array<{
    id: string
    firstName: string
    lastName: string
    email: string
    role: string
  }>>([])

  // El candidato solo depende de su id. Antes este efecto tambien dependia de
  // `user`, que al montar la pantalla llega primero en null y despues con los
  // datos de la sesion: ese segundo valor volvia a disparar el efecto, ponia
  // isLoading en true y la pantalla se recargaba sola frente al usuario.
  useEffect(() => {
    if (candidateId) {
      loadCandidateDetail()
    }
  }, [candidateId])

  /**
   * Trae el CV como blob al abrir la pestana.
   *
   * Se pide una sola vez por candidato y se libera al salir: un object URL que
   * no se revoca deja el archivo retenido en memoria mientras viva la pestana.
   */
  useEffect(() => {
    const url = candidate?.cvUrl
    if (activeTab !== 'cv' || !url) return

    let cancelado = false
    let creada: string | null = null

    setCvCargando(true)
    setCvError(null)

    fileService
      .getViewableFileUrl(url)
      .then((blobUrl) => {
        creada = blobUrl
        if (cancelado) {
          fileService.revokeViewableFileUrl(blobUrl)
          return
        }
        setCvBlobUrl(blobUrl)
      })
      .catch((errCrudo: unknown) => {
        const err = errCrudo as { message?: string }
        if (!cancelado) {
          setCvError(err.message || 'No se pudo abrir el CV.')
        }
      })
      .finally(() => {
        if (!cancelado) setCvCargando(false)
      })

    return () => {
      cancelado = true
      fileService.revokeViewableFileUrl(creada)
      setCvBlobUrl(null)
    }
  }, [activeTab, candidate?.cvUrl])

  // Los entrevistadores si dependen de los permisos, asi que este espera a que
  // la sesion este resuelta. Cargarlos de mas no vuelve a mostrar el loader.
  useEffect(() => {
    if (!candidateId || !user) return

    const isValidPermissions =
      hasAnyRole(user, ['MASTER']) ||
      userHasPermission("rh.entrevistas.base.read", user?.rbac?.effectivePermissions || [])

    if (isValidPermissions) {
      loadAvailableInterviewers()
    }
  }, [candidateId, user])

  const loadCandidateDetail = async () => {
    if (!candidateId) return

    setIsLoading(true)
    setError(null)

    try {
      // Cargar datos del candidato
      const candidateData = await apiService.getCandidateById(candidateId)
      setCandidate(candidateData)

      // La deteccion de postulacion previa no debe tumbar la ficha: si el
      // permiso o el endpoint fallan, se sigue sin la etiqueta.
      try {
        setPostulacionesPrevias(await apiService.getPreviousApplications(candidateId))
      } catch {
        setPostulacionesPrevias([])
      }

      // Los comentarios de todas las etapas (SRH-321). Si falla no debe tumbar la
      // ficha: el tab se arma con lo que ya trae.
      try {
        setComentariosDelBack(await apiService.getCandidateComments(candidateId))
      } catch {
        setComentariosDelBack(null)
      }

      try {
        // Cargar entrevista RH si existe
        const interview = await hrInterviewService.getHRInterviewByCandidate(candidateId)
        setHrInterview(interview)
      } catch (hrErrCrudo: unknown) {
        const hrErr = hrErrCrudo as {
          message?: string;
          statusCode?: number;
          status?: number;
          error?: string;
          response?: { status?: number; data?: { message?: string } };
        };
        // Silenciar errores de permisos para entrevistas RH
        if (!hrErr.message?.includes('Access denied') && !hrErr.message?.includes('Required roles')) {
          console.error('Error al cargar entrevista RH:', hrErr)
        }
      }


      // Configurar formulario de entrevista
      setInterviewForm(prev => ({
        ...prev,
        candidateId: candidateId,
        entrevistadorId: user?.id || ''
      }))

    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar detalle del candidato:', err)
      // Solo mostrar errores que NO sean de permisos
      if (!err.message?.includes('Access denied') && !err.message?.includes('Required roles')) {
        setError(err.message || 'Error al cargar los datos del candidato')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const loadAvailableInterviewers = async () => {
    try {
      const usersData = await apiService.getUsers();
      const eligibleUsers = (usersData ?? []).filter((u: any) =>
        userHasPermission("rh.entrevistas.base.create", u?.rbac?.effectivePermissions),
      );
      const normalizedInterviewers = eligibleUsers.map((u: any) => ({
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        role: u?.roles?.[0]?.name ?? u?.rbac?.roles?.[0]?.name ?? '',
      }));

      setAvailableInterviewers(normalizedInterviewers);
    } catch (err) {
      console.error('Error al cargar entrevistadores:', err)
    }
  }

  const handleInterviewFormChange = (field: keyof HRInterviewRegistration, value: string) => {
    setInterviewForm(prev => ({
      ...prev,
      [field]: value
    }))

    if (interviewErrors[field]) {
      setInterviewErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const handleResultsFormChange = (field: keyof HRInterviewResults, value: any) => {
    setResultsForm(prev => ({
      ...prev,
      [field]: value
    }))

    // Limpiar error si existe (solo para campos que tienen validación)
    if (field in resultsErrors) {
      setResultsErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const handleSubmitInterview = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmittingInterview(true)
    setInterviewErrors({})
    setError(null)

    try {
      const validation = hrInterviewService.validateInterviewRegistration(interviewForm)

      if (!validation.isValid) {
        const errors: HRInterviewFormErrors = {}
        validation.errors.forEach(error => {
          if (error.includes('candidato')) errors.candidateId = error
          if (error.includes('fecha')) errors.fechaEntrevista = error
          if (error.includes('hora')) errors.horaEntrevista = error
          if (error.includes('modalidad')) errors.modalidad = error
          if (error.includes('ubicación')) errors.ubicacionLink = error
          if (error.includes('entrevistador')) errors.entrevistadorId = error
        })
        setInterviewErrors(errors)
        return
      }

      const interview = await hrInterviewService.registerHRInterview(interviewForm)
      setHrInterview(interview)
      setShowInterviewForm(false)
      setSuccessMessage('Entrevista RH programada exitosamente')

      // Recargar candidato para actualizar estado
      await loadCandidateDetail()

      setTimeout(() => setSuccessMessage(null), 5000)

    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error al programar la entrevista')
    } finally {
      setIsSubmittingInterview(false)
    }
  }

  const handleSubmitResults = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!hrInterview) return

    setIsSubmittingResults(true)
    setResultsErrors({})
    setError(null)

    try {
      const validation = hrInterviewService.validateInterviewResults(resultsForm)

      if (!validation.isValid) {
        const errors: HRInterviewResultsFormErrors = {}
        validation.errors.forEach(error => {
          if (error.includes('resultado')) errors.resultadoEntrevistaRh = error
          if (error.includes('comentarios')) errors.comentariosEntrevistaRh = error
          if (error.includes('motivo')) errors.motivoRechazo = error
          if (error.includes('decisión')) errors.decision = error
        })
        setResultsErrors(errors)
        return
      }

      // Incluir la URL del archivo si se subió
      const resultsWithFile = {
        ...resultsForm,
        archivoPsicometricoTecnico: uploadedFileUrl || undefined
      }

      const updatedInterview = await hrInterviewService.uploadHRInterviewResults(
        hrInterview.id,
        resultsWithFile
      )
      setHrInterview(updatedInterview)
      setShowResultsForm(false)
      setUploadedFileUrl(null)
      setSuccessMessage('Resultados guardados exitosamente')

      // Recargar candidato para actualizar estado
      await loadCandidateDetail()

      setTimeout(() => setSuccessMessage(null), 5000)

    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error al guardar los resultados')
    } finally {
      setIsSubmittingResults(false)
    }
  }

  // Handler para cuando se selecciona un archivo (subida automática)
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Subir automáticamente
    setIsUploadingFile(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('folder', 'psychometric-tests')

      const token = localStorage.getItem('access_token')
      const headers: HeadersInit = {}

      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      const response = await fetch(
        `${config.apiBaseUrl}/upload/s3`,
        {
          method: 'POST',
          headers,
          body: formData,
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Error al subir el archivo')
      }

      const result = await response.json()

      if (!result.url) {
        throw new Error('No se recibió la URL del archivo')
      }

      // Guardar la URL para enviarla después con los resultados
      setUploadedFileUrl(result.url)
      setSuccessMessage('Archivo subido exitosamente. Se enviará con los resultados.')

      setTimeout(() => setSuccessMessage(null), 5000)

    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error al subir el archivo')
      setUploadedFileUrl(null)
    } finally {
      setIsUploadingFile(false)
    }
  }

  const handleDeleteInterview = async () => {
    if (!hrInterview || !window.confirm('¿Está seguro de eliminar esta entrevista? Solo se pueden eliminar entrevistas sin resultados.')) {
      return
    }

    try {
      await hrInterviewService.deleteHRInterview(hrInterview.id)
      setHrInterview(null)
      setSuccessMessage('Entrevista eliminada exitosamente')

      // Recargar candidato para actualizar estado
      await loadCandidateDetail()

      setTimeout(() => setSuccessMessage(null), 5000)

    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error al eliminar la entrevista')
    }
  }

  const handleChangeStatusToProcess = async () => {
    if (!candidateId) return

    setIsUpdatingStatus(true)
    setError(null)
    setShowStatusChangeModal(false)

    try {
      await apiService.updateCandidateStatus(candidateId, 'en_proceso')
      setStatusSuccessMessage('Estado actualizado exitosamente. Ahora puede programar la entrevista.')
      setShowStatusSuccessModal(true)

      // Recargar candidato para actualizar estado
      await loadCandidateDetail()

    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error al actualizar el estado del candidato')
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const handleRejectCandidateFromPreregistration = async () => {
    if (!candidateId) return
    if (!window.confirm('¿Desea rechazar este candidato?')) return

    setIsUpdatingStatus(true)
    setError(null)

    try {
      await apiService.updateCandidateStatus(candidateId, 'rechazado_evaluacion_rh')
      setSuccessMessage('Candidato rechazado')
      await loadCandidateDetail()
      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error al rechazar el candidato')
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  // Funciones para decisión del jefe requisitor
  const handleOpenHiringManagerDecision = (decision: 'aprobar' | 'rechazar') => {
    if (candidate?.status !== 'pre_seleccionado' && candidate?.status !== 'entrevista_solicitante') {
      setError('Este candidato ya tiene una decisión final y no se puede volver a decidir.')
      return
    }
    // Un error de un intento anterior no debe recibir al siguiente.
    setError(null)
    setHiringManagerDecision(decision)
    setShowHiringManagerDecisionModal(true)
  }

  const handleSubmitHiringManagerDecision = async () => {
    if (!candidateId || !hiringManagerDecision) return

    if (candidate?.status !== 'pre_seleccionado' && candidate?.status !== 'entrevista_solicitante') {
      setError('Este candidato ya tiene una decisión final y no se puede volver a decidir.')
      setShowHiringManagerDecisionModal(false)
      setHiringManagerDecision(null)
      return
    }

    setIsSubmittingHiringDecision(true)
    setError(null)

    try {
      // RH-03.1: aprobar ya no cierra el proceso. El candidato pasa a la
      // bandeja de RH, que es quien selecciona, lo manda a Dirección, lo
      // descarta o lo devuelve. El back rechaza 'seleccionado' por aquí.
      const newStatus = hiringManagerDecision === 'aprobar' ? 'pendiente_cierre' : 'rechazado'
      // El comentario si viaja: el modal lo exige para rechazar y antes se
      // quedaba en la pantalla, asi que el descarte se guardaba sin el motivo
      // que la persona acababa de escribir.
      await apiService.updateCandidateStatus(candidateId, newStatus, {
        comentario: hiringManagerComments.trim() || undefined,
      })

      setSuccessMessage(
        hiringManagerDecision === 'aprobar'
          ? 'Candidato aprobado. Pasó a RH para el cierre de la selección.'
          : 'Candidato rechazado'
      )

      setShowHiringManagerDecisionModal(false)
      setHiringManagerDecision(null)
      setHiringManagerComments('')

      // Recargar candidato para actualizar estado
      await loadCandidateDetail()

      setTimeout(() => setSuccessMessage(null), 5000)

    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error al actualizar el estado del candidato')
    } finally {
      setIsSubmittingHiringDecision(false)
    }
  }

  /**
   * La vacante pide la entrevista final del solicitante, así que la decisión
   * se toma al registrar el resultado de esa entrevista, no aquí.
   */
  const pideEntrevistaFinalDelSolicitante =
    candidate?.jobRequest?.entrevistaFinalSolicitante === true

  const canApproveOrRejectAsHiringManager = () => {
    // El candidato debe estar pendiente de decisión final del solicitante
    const isRequester = user && userHasPermission(
      "rh.solicitudes_personal.base.create",
      user?.rbac?.effectivePermissions || []
    )
    const appropriateStatus =
      candidate?.status === 'pre_seleccionado' ||
      candidate?.status === 'entrevista_solicitante'

    return isRequester && appropriateStatus
  }

  /**
   * Quién ve la tarjeta de la entrevista del solicitante (SRH-308).
   *
   * RH y administración agendan en nombre del solicitante, así que la
   * tarjeta ya no depende de ser quien crea solicitudes. Lo que sí sigue
   * siéndolo es decidir: los botones de aprobar y rechazar quedan solo para
   * el solicitante.
   */
  const puedeAgendarDesdeLaFicha = () => {
    const etapaCorrecta =
      candidate?.status === 'pre_seleccionado' ||
      candidate?.status === 'entrevista_solicitante'

    return (
      etapaCorrecta &&
      puedeAgendarEntrevistaDelSolicitante(user?.rbac?.effectivePermissions || [])
    )
  }

  const canChangeStatusToPreselection = () => {
    return candidate?.status === 'pre_registro' && userHasPermission(
      "rh.reclutamiento_candidatos.especial.change_status",
      user?.rbac?.effectivePermissions || []
    )
  }

  /**
   * Etiqueta en español del estatus, para el historial. Sale del mismo mapa
   * que la pastilla: antes era una copia aparte, y por eso al historial le
   * faltaban los estatus del cierre y mostraba `pendiente_direccion` crudo.
   */
  const etiquetaDeEstatus = (status: Candidate['status']) =>
    nombreDeEstatus(status).label

  /** Dias que lleva la postulacion desde que se registro. */
  const diasEnProceso = (desde: string) => {
    const inicio = comoInstante(desde).getTime()
    if (Number.isNaN(inicio)) return '—'
    return Math.max(0, Math.floor((Date.now() - inicio) / 86_400_000))
  }

  /**
   * Badge de estatus del candidato. Sin icono y en tono plano, como el diseño:
   * el color del texto lleva el peso y el fondo es apenas un tinte.
   *
   * El unico par que viene del diseño es el de En proceso (#D4A112); los demas
   * mantienen el tono que ya tenian, solo pasados a este mismo tratamiento.
   */
  const getStatusBadge = (status: Candidate['status']) => {
    const config = nombreDeEstatus(status)

    return (
      <Badge color={colorDeEstatusDeCandidato(status, 'azul')}>{config.label}</Badge>
    )
  }

  const canScheduleInterview = () => {
    const permissions = user?.rbac?.effectivePermissions || []
    return (
      userHasPermission("rh.entrevistas.base.create", permissions) ||
      userHasPermission("rh.entrevistas.base.create", permissions)
    ) &&
      candidate?.status === 'en_proceso' &&
      !hrInterview
  }

  const canManageResults = () => {
    return userHasPermission(
      "rh.entrevistas.especial.capturar_feedback",
      user?.rbac?.effectivePermissions || []
    ) && hrInterview &&
      (!hrInterview?.decision || hrInterview.decision === 'pendiente')
  }

  const canDeleteInterview = () => {
    return canConductInterviews(user, 'rh') &&
      hrInterview &&
      !hrInterview.decision
  }

  // Los estados de carga, error y vacio van DENTRO del marco. Antes se
  // devolvian sueltos, sin barra lateral ni encabezado: al entrar se veia una
  // pantalla pelona con el loader y, al llegar los datos, aparecia de golpe la
  // pantalla completa con menu. Parecia que la pagina se recargaba sola.
  if (isLoading) {
    return (
    <AuthenticatedLayout
      activeRoute="hr-interviews"
      containerClassName="w-full max-w-none px-4 sm:px-6 lg:px-8 py-8"
      user={user}
      onLogout={logout}
    >
        {/* A-01: silueta del expediente, para que al llegar los datos nada brinque. */}
        <EsqueletoDetalle campos={9} />
    </AuthenticatedLayout>
    )
  }

  if (error && !candidate) {
    return (
    <AuthenticatedLayout
      activeRoute="hr-interviews"
      containerClassName="w-full max-w-none px-4 sm:px-6 lg:px-8 py-8"
      user={user}
      onLogout={logout}
    >
        <button
          onClick={() => navigate(-1)}
          className={estiloDeBoton('secundario', 'mb-6')}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-900">Error al cargar</h3>
              <p className="text-red-800 mt-1">{error}</p>
            </div>
          </div>
        </div>
    </AuthenticatedLayout>
    )
  }

  if (!candidate) {
    return (
    <AuthenticatedLayout
      activeRoute="hr-interviews"
      containerClassName="w-full max-w-none px-4 sm:px-6 lg:px-8 py-8"
      user={user}
      onLogout={logout}
    >
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No se encontró el candidato</p>
          </div>
        </div>
    </AuthenticatedLayout>
    )
  }

  const isRequestCreator =
    Boolean(user?.id) &&
    Boolean(candidate.jobRequest?.solicitante?.id) &&
    user?.id === candidate.jobRequest?.solicitante?.id

  const canViewRhInterviewTab =
    userHasPermission(
      "rh.entrevistas.base.read",
      user?.rbac?.effectivePermissions || []
    ) || isRequestCreator

  return (
    <AuthenticatedLayout
      activeRoute="hr-interviews"
      containerClassName="w-full max-w-none px-4 sm:px-6 lg:px-8 py-8"
      user={user}
      onLogout={logout}
    >
      {/* Header con navegación. Volver y el título van en el mismo renglón, como
          en el diseño; el breadcrumb cuelga del título, no del botón. */}
      <div className="mb-6 flex items-start gap-6">
        <button
          onClick={() => navigate(-1)}
          className={estiloDeBoton('secundario', 'mt-1')}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <div>
          <TituloDePantalla nivel="interna">Ver candidato</TituloDePantalla>
          <p className="text-sm text-gray-500 mt-1">
            Panel de candidatos / Entrevistas del solicitante / Ver detalles del candidato
          </p>
        </div>
      </div>

      {/* Mensajes de éxito y error */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <p className="text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {canChangeStatusToPreselection() && (
        <div className="mb-8 w-full flex justify-end">
          <button
            onClick={handleRejectCandidateFromPreregistration}
            disabled={isUpdatingStatus}
            className={estiloDeBoton('peligro', 'mr-3')}
            title="Rechazar candidato"
          >
            <XCircle className="w-4 h-4" />
            Rechazar
          </button>
          <button
            onClick={() => setShowStatusChangeModal(true)}
            disabled={isUpdatingStatus}
            className={estiloDeBoton('primario')}
            title="Cambiar a en preselección"
          >
            <CheckCircle className="w-4 h-4" />
            Cambiar a en preselección
          </button>
        </div>
      )}

      {/* SRH-309: seleccionado a mano sin su evaluación. Está a medias hasta que
          se suba, y desde aquí se sube. */}
      {tieneEvaluacionPendiente(candidate) && candidateId && (
        <EvaluacionPendienteDelCandidato
          candidateId={candidateId}
          nombre={candidate.fullName}
          permisos={user?.rbac?.effectivePermissions}
          onSubida={async () => {
            setSuccessMessage('Evaluación subida. El candidato quedó seleccionado y se avisó a RH.')
            await loadCandidateDetail()
            setTimeout(() => setSuccessMessage(null), 5000)
          }}
        />
      )}

      {/* Sección de Información Personal - Nuevo Diseño Horizontal */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E0E0E0] p-6 mb-6">
        <div className="flex items-center justify-between -mx-6 px-6 pb-4 mb-6 border-b border-[#E0E0E0]">
          <h2 className="text-base font-semibold text-[#1E1E1F]">Información Personal</h2>
          <div className="flex items-center gap-2">
            {/* RH-07. Dice "posible" a proposito: la coincidencia es por correo
                o telefono, y dos personas pueden compartirlos. Lleva al
                historial, que es donde se puede juzgar si es la misma. */}
            {postulacionesPrevias.length > 0 && (
              <Badge
                color="amarillo"
                icon={<History className="w-3.5 h-3.5" />}
                title="Ver el historial de postulaciones"
                onClick={() => setActiveTab('historial')}
                className="cursor-pointer"
              >
                Posible postulación previa
              </Badge>
            )}
            {getStatusBadge(candidate.status)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-xs font-medium text-[#777777]">Nombre completo:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5">{candidate.fullName}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-[#777777]">Email:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5 break-words">{candidate.email}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-[#777777]">Teléfono:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5">{candidate.phone}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-[#777777]">Edad:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5">{candidate.age} años</p>
          </div>

          <div>
            <p className="text-xs font-medium text-[#777777]">Experiencia:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5">{experienciaDelCandidato(candidate.experienceYears)}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-[#777777]">Registrado:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5">
              {formatDate(candidate.createdAt)}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-[#777777]">Certificaciones:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5">
              {candidate?.certificaciones?.trim() || 'No especificadas'}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-[#777777]">Profesión:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5">
              {candidate?.profesion ?? ''}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-[#777777]">Ciudad de residencia:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5">
              {candidate.residenceCity?.trim() || 'No especificada'}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-[#777777]">Sueldo deseado:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5">
              {formatoDeSueldo(candidate.salaryExpectation)}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-[#777777]">Vacante a la que aplica:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5">
              {candidate.jobRequest?.puestoSolicitar || 'No especificado'}
            </p>
            {/* Este dato no se veía en ningún lado y es el que decide cómo se
                cierra el proceso: con él, la decisión del solicitante se
                registra al cargar el resultado de su entrevista. */}
            {pideEntrevistaFinalDelSolicitante && (
              <Badge
                color="azul"
                icon={<CalendarPlus className="w-3 h-3 flex-shrink-0" />}
                className="mt-1.5"
              >
                Requiere entrevista del solicitante
              </Badge>
            )}
          </div>

          {/* Origen declarado (RH-06), no el método de captura. */}
          <div>
            <p className="text-xs font-medium text-[#777777]">Origen:</p>
            <p className="text-sm font-normal text-[#2F2F2F] mt-0.5">
              {candidate.source?.name || SIN_ORIGEN}
            </p>
          </div>
        </div>
      </div>

      {/*
        La recomendación va arriba de los tabs y no dentro de uno: es un
        pendiente de RH —vincular— y no un dato más del expediente.
      */}
      {candidate.source?.isReferral && (
        <RecomendacionDelCandidato
          candidato={candidate}
          puedeVincular={userHasPermission(
            'rh.reclutamiento_candidatos.especial.vincular_recomendacion',
            user?.rbac?.effectivePermissions || [],
          )}
          onVinculado={loadCandidateDetail}
        />
      )}

      {/*
        Dirección (RH-03). Las tres acciones de decisión sobre el candidato,
        arriba de los tabs: es la acción principal de la pantalla, no contenido
        de un tab.

        Sustituye a la tarjeta «Decisión final de entrevista», que tenía dos de
        estas tres. Dejar las dos habría puesto en la misma pantalla dos
        tarjetas casi idénticas decidiendo lo mismo. Quién la ve no cambió: la
        misma condición de antes.
      */}
      {(canApproveOrRejectAsHiringManager() || puedeAgendarDesdeLaFicha()) && (
        <div className="bg-white rounded-xl shadow-sm border border-[#E0E0E0] p-6 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              {/* Antes decía «Dirección», pero esta tarjeta es la entrevista y la
                  decisión del SOLICITANTE; Dirección es otra etapa, con su propia
                  bandeja. Ese nombre era el que confundía a quien la leía. */}
              <h3 className="text-base font-semibold text-[#1E1E1F] mb-2">
                Entrevista del solicitante
              </h3>
              <p className="text-sm text-gray-600">
                {textoDeLaEntrevistaDelSolicitante({
                  puedeDecidir: Boolean(canApproveOrRejectAsHiringManager()),
                  pideEntrevistaFinal: pideEntrevistaFinalDelSolicitante,
                })}
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              {puedeAgendarDesdeLaFicha() && (
              <button
                onClick={() => setShowSolicitarEntrevista(true)}
                className={estiloDeBoton('primario')}
              >
                <CalendarPlus className="w-4 h-4" />
                Agendar entrevista
              </button>
              )}
              {canApproveOrRejectAsHiringManager() && (
              <button
                onClick={() => handleOpenHiringManagerDecision('rechazar')}
                className={estiloDeBoton('peligro')}
              >
                <XCircle className="w-4 h-4" />
                Rechazar
              </button>
              )}
              {/* Con entrevista final de por medio, aprobar aquí no puede
                  funcionar: el servidor la exige aprobada, y aprobarla en la
                  pantalla de entrevistas ya mueve al candidato sola. Antes el
                  botón estaba y terminaba en un 400 que ni se veía. */}
              {canApproveOrRejectAsHiringManager() && !pideEntrevistaFinalDelSolicitante && (
                <button
                  onClick={() => handleOpenHiringManagerDecision('aprobar')}
                  className={estiloDeBoton('exito')}
                >
                  <CheckCheck className="w-4 h-4" />
                  Aprobar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tabs. Mismo patron que las de configuracion: subrayado en el activo en
          vez de los botones azules grandes que habia antes. El orden es el del
          diseno nuevo: Experiencia, Entrevista RH y al final el CV. */}
      <div className="mb-6">
        <nav className="flex gap-1 border-b" style={{ borderColor: '#E0E0E0' }} aria-label="Tabs">
          <button
            onClick={() => setActiveTab('informacion-general')}
            className={`group inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${activeTab === 'informacion-general'
              ? 'border-[#477FFA] font-semibold'
              : 'border-transparent hover:border-gray-200'
              }`}
            style={{ color: '#1E1E1F' }}
          >
            Información general
          </button>
          {/* Sin el permiso el contenido de este tab no se pinta, asi que el tab
              tampoco: dejarlo llevaba a una pantalla en blanco. */}
          {canViewRhInterviewTab && (
            <button
              onClick={() => setActiveTab('entrevista-rh')}
              className={`group inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${activeTab === 'entrevista-rh'
                ? 'border-[#477FFA] font-semibold'
                : 'border-transparent hover:border-gray-200'
                }`}
              style={{ color: '#1E1E1F' }}
            >
              Entrevista RH
            </button>
          )}
          {/* El tab se muestra siempre, tenga CV o no. Escondiendolo parecia que
              la funcion se habia roto, cuando lo que pasa es que en vacantes
              tecnicas el CV es opcional y el candidato no adjunto ninguno. */}
          <button
            onClick={() => setActiveTab('cv')}
            className={`group inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${activeTab === 'cv'
              ? 'border-[#477FFA] font-semibold'
              : 'border-transparent hover:border-gray-200'
              }`}
            style={{ color: '#1E1E1F' }}
          >
            <Paperclip
              className={`h-4 w-4 flex-shrink-0 ${activeTab === 'cv' ? 'text-[#477FFA]' : 'text-gray-400 group-hover:text-gray-600'}`}
            />
            Ver CV adjunto
          </button>
          <button
            onClick={() => setActiveTab('comentarios')}
            className={`group inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${activeTab === 'comentarios'
              ? 'border-[#477FFA] font-semibold'
              : 'border-transparent hover:border-gray-200'
              }`}
            style={{ color: '#1E1E1F' }}
          >
            Comentarios
          </button>
          <button
            onClick={() => setActiveTab('historial')}
            className={`group inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${activeTab === 'historial'
              ? 'border-[#477FFA] font-semibold'
              : 'border-transparent hover:border-gray-200'
              }`}
            style={{ color: '#1E1E1F' }}
          >
            Historial
          </button>
        </nav>
      </div>


      {/* Contenido de la tab Experiencia. El diseno divide la tarjeta en
          encabezado y secciones; la trayectoria laboral por empleo no se pinta
          porque el candidato solo guarda un texto libre de experiencia. */}
      {activeTab === 'informacion-general' && (
        <InformacionGeneralDelCandidato
          fuentes={{
            candidato: candidate,
            entrevista: hrInterview,
            vacante: candidate.jobRequest ?? null,
          }}
          puedeVerDatosDeDireccion={userHasPermission(
            'rh.reclutamiento_candidatos.especial.consultar_aprobados',
            user?.rbac?.effectivePermissions || [],
          )}
          verCompensacion={puedeVerCompensacion(user || undefined)}
        />
      )}

      {/* Lo que Dirección ya entrevistó, para quien consulta la ficha. Solo
          lectura: agendar y registrar resultado viven en la vista de Dirección.
          El endpoint pide decidir o consultar aprobados, igual que la sección
          de datos de arriba, así que se pinta con el mismo permiso. */}
      {activeTab === 'informacion-general' &&
        userHasPermission(
          'rh.reclutamiento_candidatos.especial.consultar_aprobados',
          user?.rbac?.effectivePermissions || [],
        ) && (
          <div className="mt-6">
            <EntrevistasDeDireccion
              candidateId={candidateId ?? ''}
              puedeDecidir={false}
              soloConsulta
              onAgendar={() => {}}
              onRegistrarResultado={() => {}}
            />
          </div>
        )}

      {/* Contenido de la tab Entrevista RH */}
      {activeTab === 'entrevista-rh' && canViewRhInterviewTab && (
          <div className="bg-white rounded-xl shadow-sm border border-[#E0E0E0] p-6">
            <div className="flex items-center justify-between -mx-6 px-6 pb-4 mb-6 border-b border-[#E0E0E0]">
              <h2 className="text-base font-semibold text-[#1E1E1F]">Entrevista RH </h2>

              <div className="flex gap-2">
                {canManageResults() && !showResultsForm && hrInterview && (
                  <button
                    onClick={() => {
                      setShowResultsForm(true)
                      // Lo ya capturado en la entrevista, y en los huecos lo que
                      // el candidato declaro al postularse.
                      if (hrInterview.resultadoEntrevistaRh) {
                        setResultsForm(conLoDeclaradoPorElCandidato({
                          resultadoEntrevistaRh: hrInterview.resultadoEntrevistaRh,
                          comentariosEntrevistaRh: hrInterview.comentariosEntrevistaRh || '',
                          camposClaveTest: hrInterview.camposClaveTest || '',
                          previousMonthlySalary: aImporte(hrInterview.previousMonthlySalary),
                          salaryExpectation: aImporte(hrInterview.salaryExpectation),
                          employmentStatus: hrInterview.employmentStatus ?? null,
                          availability: hrInterview.availability || '',
                          residenceCity: hrInterview.residenceCity || '',
                          cumplePerfil: hrInterview.cumplePerfil || false,
                          experienciaValidada: hrInterview.experienciaValidada || false,
                          antecedentesOk: hrInterview.antecedentesOk || false,
                          decision: decisionParaElFormulario(hrInterview.decision),
                          motivoRechazo: hrInterview.motivoRechazo || ''
                        }, candidate))
                      } else {
                        // Primera captura: nace con lo que declaro al postularse.
                        setResultsForm((previos) =>
                          conLoDeclaradoPorElCandidato(previos, candidate),
                        )
                      }
                    }}
                    className={estiloDeBoton('primario')}
                  >
                    <FileText className="w-4 h-4" />
                    {hrInterview.resultadoEntrevistaRh ? 'Editar Resultados' : 'Cargar Resultados'}
                  </button>
                )}
              </div>
            </div>

            {/* Formulario de programación de entrevista */}
            {showInterviewForm && (
              <div className="mb-6 border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Nueva Entrevista RH</h3>

                <form onSubmit={handleSubmitInterview} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fecha de Entrevista *
                      </label>
                      <input
                        type="date"
                        value={interviewForm.fechaEntrevista}
                        onChange={(e) => handleInterviewFormChange('fechaEntrevista', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${interviewErrors.fechaEntrevista ? 'border-red-300' : 'border-gray-300'
                          }`}
                        required
                      />
                      {interviewErrors.fechaEntrevista && (
                        <p className="mt-1 text-xs text-red-600">{interviewErrors.fechaEntrevista}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hora *
                      </label>
                      <input
                        type="time"
                        value={interviewForm.horaEntrevista}
                        onChange={(e) => handleInterviewFormChange('horaEntrevista', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${interviewErrors.horaEntrevista ? 'border-red-300' : 'border-gray-300'
                          }`}
                        required
                      />
                      {interviewErrors.horaEntrevista && (
                        <p className="mt-1 text-xs text-red-600">{interviewErrors.horaEntrevista}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de entrevista *
                    </label>
                    <SelectBuscable
                      variante="formulario"
                      value={interviewForm.modalidad}
                      onChange={(valor) => handleInterviewFormChange('modalidad', valor)}
                      required
                      canDelete={false}
                      placeholderDeBusqueda="Buscar tipo..."
                      opciones={[
                        { value: 'presencial', label: 'Presencial' },
                        { value: 'virtual', label: 'Virtual' },
                      ]}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {interviewForm.modalidad === 'virtual' ? 'Link de la Reunión *' : 'Ubicación *'}
                    </label>
                    <input
                      type="text"
                      value={interviewForm.ubicacionLink}
                      onChange={(e) => handleInterviewFormChange('ubicacionLink', e.target.value)}
                      placeholder={interviewForm.modalidad === 'virtual' ? 'https://zoom.us/j/...' : 'Oficina central, Sala 1'}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${interviewErrors.ubicacionLink ? 'border-red-300' : 'border-gray-300'
                        }`}
                      required
                    />
                    {interviewErrors.ubicacionLink && (
                      <p className="mt-1 text-xs text-red-600">{interviewErrors.ubicacionLink}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entrevistador *
                    </label>
                    <SelectBuscable
                      variante="formulario"
                      value={interviewForm.entrevistadorId}
                      onChange={(valor) => handleInterviewFormChange('entrevistadorId', valor)}
                      error={Boolean(interviewErrors.entrevistadorId)}
                      required
                      placeholder="Seleccionar entrevistador"
                      placeholderDeBusqueda="Buscar entrevistador..."
                      opciones={availableInterviewers.map((interviewer) => ({
                        value: String(interviewer.id),
                        label: `${interviewer.firstName} ${interviewer.lastName}${interviewer.role ? ` (${interviewer.role})` : ''}`,
                      }))}
                    />
                    {interviewErrors.entrevistadorId && (
                      <p className="mt-1 text-xs text-red-600">{interviewErrors.entrevistadorId}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Observaciones
                    </label>
                    <textarea
                      value={interviewForm.observaciones}
                      onChange={(e) => handleInterviewFormChange('observaciones', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Observaciones adicionales..."
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowInterviewForm(false)}
                      className={estiloDeBoton('secundario')}
                    >
                      Cancelar
                    </button>
                    {(userHasPermission(
                      "rh.entrevistas.base.create",
                      user?.rbac?.effectivePermissions || []
                    ) || userHasPermission(
                      "rh.entrevistas.base.create",
                      user?.rbac?.effectivePermissions || []
                    )) && <button
                      type="submit"
                      disabled={isSubmittingInterview}
                      className={estiloDeBoton('primario', 'flex-1')}
                    >
                        {isSubmittingInterview ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Programando...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            Programar Entrevista
                          </>
                        )}
                      </button>}
                  </div>
                </form>
              </div>
            )}

            {/* Información de entrevista programada */}
            {hrInterview && (
              <div className="space-y-6">
                {/* Solo mostrar la card cuando NO esté el formulario de resultados */}
                {!showResultsForm && (
                  <div>
                    {/* Sin titulo propio: el de la card ya dice Entrevista RH y
                        repetirlo dejaba dos encabezados iguales encimados. */}
                    {/* Sin badge de la decision de la entrevista: el estatus del
                        candidato ya sale arriba y dos badges de colores distintos
                        para lo mismo confundian. */}
                    {canDeleteInterview() && (
                      <div className="flex items-center justify-end mb-4">
                        <button
                          onClick={handleDeleteInterview}
                          className="text-red-600 hover:text-red-700 p-1"
                          title="Eliminar entrevista"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {/* Resumen de la entrevista, como en el diseño: tres tarjetas
                        en gris claro y los comentarios abajo a todo lo ancho. */}
                    {hrInterview.decision ? (
                      <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Column 1: Entrevista Programada */}
                        <div className="bg-[#F8F9FB] rounded-xl p-5">
                          <h5 className="text-sm font-semibold text-[#1E1E1F] mb-3">Entrevista programada</h5>

                          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5 text-sm text-[#2F2F2F]">
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin className="w-4 h-4 text-[#777777] flex-shrink-0" />
                              {hrInterview.ubicacionLink}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <Calendar className="w-4 h-4 text-[#777777] flex-shrink-0" />
                              {parseDateSafe(hrInterview.fechaEntrevista)?.toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}, {hrInterview.horaEntrevista?.slice(0, 5)}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-[#777777] mb-1">Tipo de entrevista:</p>
                              <p className="text-sm text-[#2F2F2F] capitalize">{hrInterview.modalidad}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#777777] mb-1">Entrevistador:</p>
                              <p className="text-sm text-[#2F2F2F]">
                                {nombreDeUsuario(
                                  hrInterview.entrevistador ?? hrInterview.entrevistadorHistorico,
                                )}
                              </p>
                            </div>
                          </div>

                          {hrInterview.observaciones && (
                            <div className="mt-4">
                              <p className="text-xs text-[#777777] mb-1">Observaciones:</p>
                              <p className="text-sm text-[#2F2F2F]">{hrInterview.observaciones}</p>
                            </div>
                          )}
                        </div>

                        {/* Column 2: Resultado de entrevista RH */}
                        <div className="bg-[#F8F9FB] rounded-xl p-5">
                          <h5 className="text-sm font-semibold text-[#1E1E1F] mb-3">Resultado de entrevista RH:</h5>

                          {/* A-22: la cifra y la barra crecen cuando la tarjeta
                              entra en pantalla; una barra ya puesta se lee como
                              adorno, una que crece se lee como medición. */}
                          <p className="text-sm text-[#777777] mb-2">
                            {avance.valor}/10
                          </p>
                          <div ref={avance.ref} className="w-full bg-[#E9EAEC] rounded-full h-2.5">
                            <div
                              className="bg-[#5FC98D] h-2.5 rounded-full"
                              style={{ width: `${(avance.valor / 10) * 100}%` }}
                            />
                          </div>

                          {hrInterview.archivoPsicometricoTecnico && (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await fileService.downloadFile(
                                    hrInterview.archivoPsicometricoTecnico!,
                                    `test_psicometrico_${candidate?.fullName}.pdf`
                                  )
                                } catch {
                                  setError('Error al descargar el archivo')
                                }
                              }}
                              className="inline-flex items-center mt-5 text-sm text-[#477FFA] hover:underline"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Ver archivo de test
                            </button>
                          )}
                        </div>

                        {/* Column 3: Checklist RH */}
                        <div className="bg-[#F8F9FB] rounded-xl p-5">
                          <h5 className="text-sm font-semibold text-[#1E1E1F] mb-3">Checklist RH:</h5>

                          <div className="space-y-2.5">
                            {[
                              { ok: hrInterview.cumplePerfil, texto: 'Cumple con el perfil' },
                              { ok: hrInterview.experienciaValidada, texto: 'Experiencia validada' },
                              { ok: hrInterview.antecedentesOk, texto: 'Antecedentes OK' },
                            ].map((item) => (
                              <div key={item.texto} className="flex items-center gap-2.5">
                                <CheckCircle
                                  className={`w-4 h-4 flex-shrink-0 ${item.ok ? 'text-[#22C55E]' : 'text-[#C9CDD3]'}`}
                                />
                                <span className={`text-sm ${item.ok ? 'text-[#2F2F2F]' : 'text-[#9CA3AF] line-through'}`}>
                                  {item.texto}
                                </span>
                              </div>
                            ))}
                          </div>

                          {hrInterview.motivoRechazo && (
                            <div className="mt-4 p-3 bg-[#FBEEEE] rounded-lg">
                              <p className="text-xs font-medium text-[#DC2626] mb-1">Motivo de rechazo:</p>
                              <p className="text-sm text-[#DC2626]">{hrInterview.motivoRechazo}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Comentarios de la entrevista, a todo lo ancho */}
                      {(hrInterview.comentariosEntrevistaRh || hrInterview.camposClaveTest) && (
                        <div className="bg-[#F8F9FB] rounded-xl p-5 mt-4 space-y-4">
                          <h5 className="text-sm font-semibold text-[#1E1E1F]">Comentarios de la entrevista</h5>

                          {hrInterview.comentariosEntrevistaRh && (
                            <div>
                              <p className="text-xs text-[#777777] mb-1">Comentarios de entrevista</p>
                              <p className="text-sm text-[#2F2F2F] leading-relaxed whitespace-pre-wrap">
                                {hrInterview.comentariosEntrevistaRh}
                              </p>
                            </div>
                          )}

                          {hrInterview.camposClaveTest && (
                            <div>
                              <p className="text-xs text-[#777777] mb-1">Campos clave del test</p>
                              <p className="text-sm text-[#2F2F2F] leading-relaxed whitespace-pre-wrap">
                                {hrInterview.camposClaveTest}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Datos del candidato (B2). Se pinta solo si se capturo
                          alguno; si no, no tiene caso una tabla de guiones. */}
                      <div className="bg-[#F8F9FB] rounded-xl p-5 mt-4 space-y-4 empty:hidden">
                        <DatosDelCandidatoEnConsulta datos={hrInterview} />
                      </div>
                      </>
                    ) : (
                      /* Layout simple cuando no hay decisión */
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Fecha y Hora</p>
                            <div className="flex items-center text-gray-900">
                              <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                              <span className="font-medium">
                                {formatDate(hrInterview.fechaEntrevista)}
                              </span>
                              <Clock className="w-4 h-4 ml-4 mr-2 text-blue-600" />
                              <span className="font-medium">{hrInterview.horaEntrevista}</span>
                            </div>
                          </div>

                          <div>
                            <p className="text-sm text-gray-600 mb-1">Tipo de entrevista</p>
                            <div className="flex items-center text-gray-900">
                              {hrInterview.modalidad === 'virtual' ? (
                                <>
                                  <Video className="w-4 h-4 mr-2 text-blue-600" />
                                  <span className="font-medium">Virtual</span>
                                </>
                              ) : (
                                <>
                                  <MapPin className="w-4 h-4 mr-2 text-blue-600" />
                                  <span className="font-medium">Presencial</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div>
                          <p className="text-sm text-gray-600 mb-1">
                            {hrInterview.modalidad === 'virtual' ? 'Link de Reunión' : 'Ubicación'}
                          </p>
                          {hrInterview.modalidad === 'virtual' ? (
                            <a
                              href={hrInterview.ubicacionLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 underline font-medium"
                            >
                              {hrInterview.ubicacionLink}
                            </a>
                          ) : (
                            <p className="text-gray-900 font-medium">{hrInterview.ubicacionLink}</p>
                          )}
                        </div>

                        <div>
                          <p className="text-sm text-gray-600 mb-1">Entrevistador</p>
                          <div className="flex items-center text-gray-900">
                            <User className="w-4 h-4 mr-2 text-blue-600" />
                            {/* Puede no existir: si la cuenta del entrevistador
                                se dio de baja, la entrevista conserva su nombre
                                pero ya no hay usuario al que apuntar. */}
                            <span className="font-medium">
                              {nombreDeUsuario(
                                hrInterview.entrevistador ?? hrInterview.entrevistadorHistorico,
                              )}
                            </span>
                            {(hrInterview.entrevistador?.email ??
                              hrInterview.entrevistadorHistorico?.email) && (
                              <span className="ml-2 text-sm text-gray-500">
                                (
                                {hrInterview.entrevistador?.email ??
                                  hrInterview.entrevistadorHistorico?.email}
                                )
                              </span>
                            )}
                          </div>
                        </div>

                        {hrInterview.observaciones && (
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Observaciones</p>
                            <p className="text-gray-900 text-sm">{hrInterview.observaciones}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Mensaje cuando no hay resultados */}
                {!hrInterview.decision && canManageResults() && !showResultsForm && (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">Aún no se han cargado los resultados de la entrevista</p>
                    <button
                      onClick={() => {
                        setShowResultsForm(true)
                        // Lo ya capturado en la entrevista, y en los huecos lo que
                        // el candidato declaro al postularse.
                        if (hrInterview.resultadoEntrevistaRh) {
                          setResultsForm(conLoDeclaradoPorElCandidato({
                            resultadoEntrevistaRh: hrInterview.resultadoEntrevistaRh,
                            comentariosEntrevistaRh: hrInterview.comentariosEntrevistaRh || '',
                            camposClaveTest: hrInterview.camposClaveTest || '',
                            previousMonthlySalary: aImporte(hrInterview.previousMonthlySalary),
                            salaryExpectation: aImporte(hrInterview.salaryExpectation),
                            employmentStatus: hrInterview.employmentStatus ?? null,
                            availability: hrInterview.availability || '',
                            residenceCity: hrInterview.residenceCity || '',
                            cumplePerfil: hrInterview.cumplePerfil || false,
                            experienciaValidada: hrInterview.experienciaValidada || false,
                            antecedentesOk: hrInterview.antecedentesOk || false,
                            decision: decisionParaElFormulario(hrInterview.decision),
                            motivoRechazo: hrInterview.motivoRechazo || ''
                          }, candidate))
                        } else {
                          // Primera captura: nace con lo que declaro al postularse.
                          setResultsForm((previos) =>
                            conLoDeclaradoPorElCandidato(previos, candidate),
                          )
                        }
                      }}
                      className={estiloDeBoton('primario')}
                    >
                      <Plus className="w-4 h-4" />
                      Cargar Resultados
                    </button>
                  </div>
                )}

                {/* Formulario de resultados */}
                {showResultsForm && (
                  <div className="bg-white border border-[#E0E0E0] rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Cargar Resultados de Entrevista</h3>

                    <form onSubmit={handleSubmitResults} className="space-y-6">
                      {/* Resultado de entrevista RH */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Resultado de Entrevista RH (1-10) *
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            min="1"
                            max="10"
                            value={resultsForm.resultadoEntrevistaRh}
                            onChange={(e) => handleResultsFormChange('resultadoEntrevistaRh', parseInt(e.target.value))}
                            className="flex-1"
                          />
                          <span className="text-2xl font-bold text-blue-600 min-w-[3rem] text-center">
                            {resultsForm.resultadoEntrevistaRh}
                          </span>
                        </div>
                        {resultsErrors.resultadoEntrevistaRh && (
                          <p className="mt-1 text-xs text-red-600">{resultsErrors.resultadoEntrevistaRh}</p>
                        )}
                      </div>

                      {/* Comentarios de entrevista */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Comentarios de Entrevista *
                        </label>
                        <textarea
                          value={resultsForm.comentariosEntrevistaRh}
                          onChange={(e) => handleResultsFormChange('comentariosEntrevistaRh', e.target.value)}
                          rows={4}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${resultsErrors.comentariosEntrevistaRh ? 'border-red-300' : 'border-gray-300'
                            }`}
                          placeholder="Describa los resultados de la entrevista..."
                          required
                        />
                        {resultsErrors.comentariosEntrevistaRh && (
                          <p className="mt-1 text-xs text-red-600">{resultsErrors.comentariosEntrevistaRh}</p>
                        )}
                      </div>

                      {/* Campos clave del test */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Resultados Principales del Test
                        </label>
                        <textarea
                          value={resultsForm.camposClaveTest}
                          onChange={(e) => handleResultsFormChange('camposClaveTest', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Ej: Puntuación: 85/100, Personalidad: Analítico..."
                        />
                      </div>

                      {/* Datos del candidato (B2) */}
                      <div className="pt-2 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-800 mb-3">
                          Datos del candidato
                        </h4>
                        <DatosDelCandidato
                          valores={resultsForm}
                          onChange={handleResultsFormChange}
                        />
                      </div>

                      {/* Upload de archivo */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Archivo Psicométrico/Técnico
                        </label>

                        {/* Mensaje de archivo subido */}
                        {uploadedFileUrl && (
                          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                            <div className="flex items-center text-green-700">
                              <CheckCircle className="w-5 h-5 mr-2" />
                              <span className="text-sm font-medium">
                                Archivo listo para enviar con los resultados
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setUploadedFileUrl(null)
                              }}
                              className="text-green-600 hover:text-green-800"
                            >
                              <XCircle className="w-5 h-5" />
                            </button>
                          </div>
                        )}

                        {/* Input de archivo */}
                        {!uploadedFileUrl && (
                          <>
                            <div className="relative">
                              <input
                                type="file"
                                onChange={handleFileSelect}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                disabled={isUploadingFile}
                                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                              {isUploadingFile && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                </div>
                              )}
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              {isUploadingFile
                                ? 'Subiendo archivo...'
                                : 'Formatos permitidos: PDF, DOC, DOCX, JPG, PNG (máx. 10MB)'}
                            </p>
                          </>
                        )}
                      </div>

                      {/* Checklist RH */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Checklist RH *
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                            <Checkbox
                              checked={resultsForm.cumplePerfil}
                              onChange={(e) => handleResultsFormChange('cumplePerfil', e.target.checked)}
                            />
                            <span className="ml-3 text-sm text-gray-900">Cumple con el perfil</span>
                          </label>

                          <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                            <Checkbox
                              checked={resultsForm.experienciaValidada}
                              onChange={(e) => handleResultsFormChange('experienciaValidada', e.target.checked)}
                            />
                            <span className="ml-3 text-sm text-gray-900">Experiencia validada</span>
                          </label>

                          <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                            <Checkbox
                              checked={resultsForm.antecedentesOk}
                              onChange={(e) => handleResultsFormChange('antecedentesOk', e.target.checked)}
                            />
                            <span className="ml-3 text-sm text-gray-900">Antecedentes OK</span>
                          </label>
                        </div>
                      </div>

                      {/* Decisión */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Decisión *
                        </label>
                        {/* Sin `required`: quien valida es la pantalla, que pinta
                            el mensaje en español debajo del campo. */}
                        <SelectBuscable
                          variante="formulario"
                          value={resultsForm.decision}
                          onChange={(valor) => handleResultsFormChange('decision', valor as DecisionDeEntrevistaEnCaptura)}
                          error={Boolean(resultsErrors.decision)}
                          canDelete={false}
                          placeholder="Selecciona una decisión"
                          placeholderDeBusqueda="Buscar decisión..."
                          opciones={[
                            { value: 'aprobado', label: 'Aprobado por RH' },
                            { value: 'rechazado', label: 'Rechazado' },
                            { value: 'pendiente', label: 'Pendiente · decido después' },
                          ]}
                        />
                        {resultsErrors.decision && (
                          <p className="mt-1 text-xs text-red-600">{resultsErrors.decision}</p>
                        )}
                      </div>

                      {/* Motivo de rechazo */}
                      {resultsForm.decision === 'rechazado' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Motivo de Rechazo *
                          </label>
                          <textarea
                            value={resultsForm.motivoRechazo}
                            onChange={(e) => handleResultsFormChange('motivoRechazo', e.target.value)}
                            rows={3}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${resultsErrors.motivoRechazo ? 'border-red-300' : 'border-gray-300'
                              }`}
                            placeholder="Explique el motivo del rechazo..."
                            required
                          />
                          {resultsErrors.motivoRechazo && (
                            <p className="mt-1 text-xs text-red-600">{resultsErrors.motivoRechazo}</p>
                          )}
                        </div>
                      )}

                      <div className="flex gap-3 pt-4 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => setShowResultsForm(false)}
                          className={estiloDeBoton('secundario')}
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmittingResults}
                          className={estiloDeBoton('primario', 'flex-1')}
                        >
                          {isSubmittingResults ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Guardando...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              Guardar Resultados
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}

            {/* Estado sin entrevista */}
            {!hrInterview && !showInterviewForm && (
              <div className="text-center py-12">
                {/* Ilustración SVG - Calendario con reloj */}
                <div className="mb-6 flex justify-center">
                  <svg width="280" height="200" viewBox="0 0 280 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Fondo decorativo azul claro */}
                    <ellipse cx="140" cy="160" rx="120" ry="35" fill="#DBEAFE" opacity="0.6" />

                    {/* Calendario principal */}
                    <g transform="translate(60, 40)">
                      {/* Cuerpo del calendario */}
                      <rect x="20" y="20" width="120" height="100" rx="8" fill="#3B82F6" />
                      <rect x="20" y="20" width="120" height="30" rx="8" fill="#2563EB" />
                      <rect x="20" y="20" width="120" height="8" rx="4" fill="#2563EB" />

                      {/* Anillas del calendario */}
                      <circle cx="45" cy="15" r="5" fill="#1E40AF" />
                      <rect x="40" y="10" width="10" height="15" fill="#3B82F6" />
                      <circle cx="115" cy="15" r="5" fill="#1E40AF" />
                      <rect x="110" y="10" width="10" height="15" fill="#3B82F6" />

                      {/* Días del calendario */}
                      <rect x="30" y="60" width="15" height="12" rx="2" fill="#60A5FA" />
                      <rect x="50" y="60" width="15" height="12" rx="2" fill="#60A5FA" />
                      <rect x="70" y="60" width="15" height="12" rx="2" fill="#60A5FA" />
                      <rect x="90" y="60" width="15" height="12" rx="2" fill="#60A5FA" />
                      <rect x="110" y="60" width="15" height="12" rx="2" fill="#60A5FA" />

                      <rect x="30" y="78" width="15" height="12" rx="2" fill="#60A5FA" />
                      <rect x="50" y="78" width="15" height="12" rx="2" fill="#60A5FA" />
                      <rect x="70" y="78" width="15" height="12" rx="2" fill="#93C5FD" />
                      <rect x="90" y="78" width="15" height="12" rx="2" fill="#93C5FD" />
                      <rect x="110" y="78" width="15" height="12" rx="2" fill="#93C5FD" />

                      <rect x="30" y="96" width="15" height="12" rx="2" fill="#60A5FA" />
                      <rect x="50" y="96" width="15" height="12" rx="2" fill="#60A5FA" />
                      <rect x="70" y="96" width="15" height="12" rx="2" fill="#93C5FD" />
                      <rect x="90" y="96" width="15" height="12" rx="2" fill="#93C5FD" />
                    </g>

                    {/* Reloj */}
                    <g transform="translate(170, 100)">
                      <circle cx="35" cy="35" r="30" fill="white" stroke="#3B82F6" strokeWidth="3" />
                      <circle cx="35" cy="35" r="3" fill="#2563EB" />
                      <line x1="35" y1="35" x2="35" y2="20" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" />
                      <line x1="35" y1="35" x2="47" y2="35" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" />
                    </g>

                    {/* Diana con flecha (objetivo) */}
                    <g transform="translate(10, 50)">
                      <circle cx="30" cy="30" r="25" fill="#DBEAFE" />
                      <circle cx="30" cy="30" r="18" fill="#93C5FD" />
                      <circle cx="30" cy="30" r="11" fill="#3B82F6" />
                      <circle cx="30" cy="30" r="5" fill="#1E40AF" />
                      {/* Flecha */}
                      <path d="M 45 15 L 35 25 L 38 28 L 48 18 Z" fill="#10B981" />
                      <line x1="35" y1="25" x2="28" y2="32" stroke="#10B981" strokeWidth="2" />
                      <path d="M 26 34 L 28 32 L 30 34 L 28 36 Z" fill="#10B981" />
                    </g>
                  </svg>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Sin entrevista programada
                </h3>

                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {canScheduleInterview()
                    ? 'Este candidato aún no tiene entrevista programada con Recursos Humanos.'
                    : canConductInterviews(user, 'rh')
                      ? 'Este candidato aún no tiene entrevista programada con Recursos Humanos.'
                      : 'No tienes permisos para programar entrevistas.'}
                </p>

                {canScheduleInterview() ? (
                  <button
                    onClick={() => setShowInterviewForm(true)}
                    className={estiloDeBoton('primario')}
                  >
                    <Plus className="w-4 h-4" />
                    Programar entrevista
                  </button>
                ) : null}
              </div>
            )}
          </div>
        )}

      {/* Contenido de la tab CV */}
      {activeTab === 'cv' && (
        <div className="bg-white rounded-xl shadow-sm border border-[#E0E0E0]">
          <div className="px-6 py-4 border-b border-[#E0E0E0]">
            <h2 className="text-base font-semibold text-[#1E1E1F]">Ver CV adjunto</h2>
          </div>
          <div className="p-6">
          {candidate.cvUrl ? (
            <div className="w-full h-[800px]">
              {cvCargando ? (
                <div className="w-full h-full flex items-center justify-center rounded-lg border border-gray-300">
                  <p className="text-sm text-gray-500">Cargando el CV…</p>
                </div>
              ) : cvError ? (
                <div className="w-full h-full flex items-center justify-center rounded-lg border border-gray-300 px-6 text-center">
                  <div>
                    <p className="text-sm font-medium text-red-600">{cvError}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Vuelve a entrar a la pestaña para reintentar.
                    </p>
                  </div>
                </div>
              ) : cvBlobUrl ? (
                <iframe
                  src={cvBlobUrl}
                  className="w-full h-full rounded-lg border border-gray-300"
                  title="CV del candidato"
                />
              ) : null}
            </div>
          ) : (
            <div className="py-16 text-center">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-900 font-medium">Este candidato no adjuntó CV</p>
              <p className="text-sm text-gray-500 mt-1">
                En las vacantes de perfil técnico el curriculum es opcional, así que el
                candidato pudo registrarse sin subirlo.
              </p>
            </div>
          )}
          </div>
        </div>
      )}

      {/* Contenido de la tab Comentarios (SRH-321). La lista llega armada del back,
          con autor y etapa de cada comentario. Si el endpoint no responde, se cae
          a lo que la ficha ya trae: la entrevista RH y el descarte. */}
      {activeTab === 'comentarios' && (() => {
        const comentarios = comentariosDelBack
          ? comentariosDeLaFicha(comentariosDelBack)
          : ([
              hrInterview?.comentariosEntrevistaRh && {
                autor: `${hrInterview.entrevistador?.firstName ?? ''} ${hrInterview.entrevistador?.lastName ?? ''}`.trim() || 'Entrevistador',
                tipo: 'Entrevista RH',
                fecha: hrInterview.fechaEntrevista,
                texto: hrInterview.comentariosEntrevistaRh,
              },
              candidate.descarteComentario && {
                autor: `${candidate.descartadoPor?.firstName ?? ''} ${candidate.descartadoPor?.lastName ?? ''}`.trim() || 'Reclutamiento',
                tipo: candidate.descarteMotivo || 'Descarte',
                fecha: candidate.descartadoEn,
                texto: candidate.descarteComentario,
              },
            ].filter(Boolean) as Array<{ autor: string; tipo: string; fecha?: string | null; texto: string }>)

        return (
          <div className="bg-white rounded-xl shadow-sm border border-[#E0E0E0]">
            <div className="px-6 py-4 border-b border-[#E0E0E0]">
              <h2 className="text-base font-semibold text-[#1E1E1F]">Comentarios</h2>
            </div>

            {comentarios.length === 0 ? (
              <div className="py-16 text-center">
                <MessageSquare className="w-10 h-10 text-[#C9CDD3] mx-auto mb-3" />
                <p className="text-[#1E1E1F] font-medium">Todavía no hay comentarios</p>
                <p className="text-sm text-[#777777] mt-1">
                  Aquí aparecen los comentarios de RH, del solicitante y del descarte.
                </p>
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {comentarios.map((comentario, i) => (
                  <div key={i}>
                    <div className="flex items-baseline justify-between gap-4 mb-2">
                      <p className="text-sm">
                        <span className="font-semibold text-[#1E1E1F]">{comentario.autor}</span>
                        <span className="text-[#777777]"> · {comentario.tipo}</span>
                      </p>
                      {comentario.fecha && (
                        <span className="text-sm text-[#777777] flex-shrink-0">
                          {formatDate(comentario.fecha)}
                        </span>
                      )}
                    </div>
                    <div className="border-l-2 border-[#E0E0E0] pl-4">
                      <p className="bg-[#F8F9FB] rounded-lg p-4 text-sm text-[#2F2F2F] leading-relaxed whitespace-pre-wrap">
                        {comentario.texto}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })()}

      {/* Contenido de la tab Historial. Solo se puede pintar la postulacion en
          curso: cada postulacion es una fila de candidato independiente y no hay
          forma de agrupar las de una misma persona sin un endpoint nuevo. */}
      {activeTab === 'historial' && (
        <div className="bg-white rounded-xl shadow-sm border border-[#E0E0E0]">
          <div className="px-6 py-4 border-b border-[#E0E0E0]">
            <h2 className="text-base font-semibold text-[#1E1E1F]">Postulaciones anteriores</h2>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-[#F8F9FB] rounded-xl p-5">
              <div className="flex items-start justify-between gap-4 mb-4">
                <p className="text-sm">
                  <span className="font-semibold text-[#1E1E1F]">
                    {candidate.jobRequest?.puestoSolicitar || 'Vacante'}
                  </span>
                  <span className="text-[#777777]">
                    {' '}ID {candidate.jobRequest?.id} · {candidate.jobRequest?.areaVacante} · desde{' '}
                    {formatDate(candidate.createdAt)}
                  </span>
                </p>
                <Badge color="azul" className="flex-shrink-0">
                  Proceso actual
                </Badge>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-[#777777] mb-1">Etapa actual:</p>
                  <p className="text-sm text-[#2F2F2F]">{etiquetaDeEstatus(candidate.status)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#777777] mb-1">Entrevistador RH:</p>
                  <p className="text-sm text-[#2F2F2F]">
                    {hrInterview
                      ? `${hrInterview.entrevistador?.firstName ?? ''} ${hrInterview.entrevistador?.lastName ?? ''}`.trim()
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#777777] mb-1">Entrevistas:</p>
                  <p className="text-sm text-[#2F2F2F]">{hrInterview ? 1 : 0}</p>
                </div>
                <div>
                  <p className="text-xs text-[#777777] mb-1">Días en proceso:</p>
                  <p className="text-sm text-[#2F2F2F]">{diasEnProceso(candidate.createdAt)}</p>
                </div>
              </div>

              {candidate.descarteComentario && (
                <div className="mt-4">
                  <p className="text-xs text-[#777777] mb-1">Motivo de descarte:</p>
                  <p className="text-sm text-[#2F2F2F] leading-relaxed">{candidate.descarteComentario}</p>
                </div>
              )}
            </div>

            {/* RH-07: las anteriores de la misma persona, por coincidencia de
                correo o telefono. Mismo formato de tarjeta que el proceso
                actual, en gris y sin acciones, porque esto es de solo lectura:
                no se fusiona nada ni se puede intervenir esa postulacion. */}
            {postulacionesPrevias.length === 0 ? (
              <p className="text-sm text-[#777777]">
                No se encontraron postulaciones anteriores de esta persona.
              </p>
            ) : (
              postulacionesPrevias.map((previa) => (
                <div key={previa.candidateId} className="bg-[#F8F9FB] rounded-xl p-5">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <p className="text-sm">
                      <span className="font-semibold text-[#1E1E1F]">
                        {previa.position || 'Vacante'}
                      </span>
                      <span className="text-[#777777]">
                        {' '}ID {previa.jobRequestId}
                        {previa.area ? ` · ${previa.area}` : ''} · desde{' '}
                        {formatDate(previa.appliedAt)}
                      </span>
                    </p>
                    <Badge color={colorDeEstatusDeCandidato(previa.status, 'azul')} className="flex-shrink-0">
                      {etiquetaDeEstatus(previa.status as Candidate['status'])}
                    </Badge>
                  </div>

                  {/* La etapa y el motivo solo existen si la postulacion
                      termino en descarte: es el unico momento en que se
                      registran. En las demas —seleccionado, contratado— el
                      resultado ya dice hasta donde llego, asi que mostrar los
                      campos en guion se leia como dato faltante. */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {previa.stageReached && (
                      <div>
                        <p className="text-xs text-[#777777] mb-1">Etapa alcanzada:</p>
                        <p className="text-sm text-[#2F2F2F]">
                          {etiquetaDeEstatus(previa.stageReached as Candidate['status'])}
                        </p>
                      </div>
                    )}
                    {previa.discardReason && (
                      <div>
                        <p className="text-xs text-[#777777] mb-1">Motivo de descarte:</p>
                        <p className="text-sm text-[#2F2F2F]">{previa.discardReason}</p>
                      </div>
                    )}
                    <div className="md:col-span-2">
                      <p className="text-xs text-[#777777] mb-1">Coincide por:</p>
                      <p className="text-sm text-[#2F2F2F]">
                        {previa.matchedBy
                          .map((via) => (via === 'email' ? 'correo' : 'teléfono'))
                          .join(' y ') || '—'}
                      </p>
                    </div>
                  </div>

                  {previa.discardComment && (
                    <div className="mt-4">
                      <p className="text-xs text-[#777777] mb-1">Comentario:</p>
                      <p className="text-sm text-[#2F2F2F] leading-relaxed">
                        {previa.discardComment}
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Cambio de Estado */}
      {showStatusChangeModal && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fadeIn">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Cambiar a En preselección
                </h3>
                <p className="text-sm text-gray-600">
                  ¿Desea cambiar el estado del candidato a <span className="font-medium text-blue-700">"preseleccionado"</span>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Esta acción permitirá programar entrevistas RH para este candidato.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowStatusChangeModal(false)}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              {userHasPermission(
                "rh.reclutamiento_candidatos.especial.change_status",
                user?.rbac?.effectivePermissions || []
              ) && <button
                onClick={handleChangeStatusToProcess}
                className={estiloDeBoton('primario')}
              >
                  <CheckCircle className="w-4 h-4" />
                  Cambiar a preseleccionado
                </button>}
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Modal de Éxito de Cambio de Estado */}
      {showStatusSuccessModal && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fadeIn">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  ¡Actualización exitosa!
                </h3>
                <p className="text-sm text-gray-600">
                  {statusSuccessMessage || 'El candidato fue cambiado a en preselección exitosamente.'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end mt-6">
              <button
                onClick={() => {
                  setShowStatusSuccessModal(false)
                  setStatusSuccessMessage('')
                }}
                className={estiloDeBoton('primario')}
              >
                Aceptar
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Modal de Decisión del Jefe Requisitor */}
      <ScheduleHiringManagerInterview
        isOpen={showSolicitarEntrevista}
        onClose={() => setShowSolicitarEntrevista(false)}
        onSuccess={async () => {
          setShowSolicitarEntrevista(false)
          setSuccessMessage('Entrevista agendada correctamente.')
          await loadCandidateDetail()
          setTimeout(() => setSuccessMessage(null), 5000)
        }}
        preSelectedCandidateId={candidateId}
      />

      {showHiringManagerDecisionModal && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fadeIn">
            <div className="flex items-start mb-4">
              <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${hiringManagerDecision === 'aprobar' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                {hiringManagerDecision === 'aprobar' ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-600" />
                )}
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {hiringManagerDecision === 'aprobar'
                    ? 'Aprobar candidato'
                    : 'Rechazar Candidato'}
                </h3>
                <p className="text-sm text-gray-600">
                  {hiringManagerDecision === 'aprobar'
                    ? '¿Aprobar a este candidato? Pasa a RH para el cierre de la selección; RH decide si lo selecciona o lo manda a validación de Dirección.'
                    : '¿Estás seguro de que deseas rechazar a este candidato? Esta acción actualizará el estado del candidato a "Rechazado".'}
                </p>
              </div>
            </div>

            {/* Campo de comentarios opcional */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comentarios {hiringManagerDecision === 'rechazar' ? '(requerido)' : '(opcional)'}
              </label>
              <textarea
                value={hiringManagerComments}
                onChange={(e) => setHiringManagerComments(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={hiringManagerDecision === 'rechazar'
                  ? 'Indica el motivo del rechazo...'
                  : 'Agrega comentarios adicionales (opcional)...'}
              />
            </div>

            {/* El aviso de error va dentro del modal. Vivia arriba de la
                pantalla, debajo de la capa, asi que un rechazo del servidor no
                se veia: el modal se quedaba abierto y parecia que no habia
                pasado nada. */}
            {error && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowHiringManagerDecisionModal(false)
                  setHiringManagerDecision(null)
                  setHiringManagerComments('')
                  setError(null)
                }}
                disabled={isSubmittingHiringDecision}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmitHiringManagerDecision}
                disabled={isSubmittingHiringDecision || (hiringManagerDecision === 'rechazar' && !hiringManagerComments.trim())}
                className={estiloDeBoton(hiringManagerDecision === 'aprobar' ? 'exito' : 'peligro')}
              >
                {isSubmittingHiringDecision ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Procesando...
                  </>
                ) : (
                  <>
                    {hiringManagerDecision === 'aprobar' ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    Confirmar
                  </>
                )}
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}
    </AuthenticatedLayout>
  )
}

export default CandidateDetailView
